import { CaseReducer, CaseReducerActions, PayloadAction } from '@reduxjs/toolkit';
import { LogEvent, PatchLogEventPayload } from 'interfaces';
import * as R from 'ramda';
import { useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { ResponsePayload } from 'slices/subscriptive';
import { createRelativeSubscriptiveSlice, RootState as ModelRootState } from 'slices/subscriptive/relative';
import { Socket } from 'socket.io-client';
import { RootState } from 'store/reducer';
import { emitAsyncOrdered } from 'utils/socket';
import { setError } from './errors';

const {
  select,
  selectChildResourceList,
  unsubscribe,
  reducer,
  reconnect,
  onPublish,
  onPartial,
  setLoadingResources,
  subscribe,
  slice,
  resourceId,
} = createRelativeSubscriptiveSlice({
  name: 'patchLogEvents',
  parentName: 'manualPatches',
  parentSingleName: 'manualPatch',
  payloadType: PatchLogEventPayload,
  deletedFilterFn(resource): boolean {
    return resource.deletedAt != null;
  },
  reducers: {
    onValidation: (state, { payload: { data: resources, manualPatchId } }: onValidatePayloadAction) => {
      if (!state[manualPatchId]) {
        return;
      }

      const patchLogEvents = state[manualPatchId].resourceDictionary;
      const indexedErrors = R.indexBy(R.prop('id'), resources);
      const updatePatchLogEvents: Record<string, PatchLogEventPayload> = {};

      for (const id in patchLogEvents) {
        updatePatchLogEvents[id] = {
          ...(patchLogEvents[id] as PatchLogEventPayload),
          validation: indexedErrors[id],
        };
      }

      Object.assign(state[manualPatchId].resourceDictionary, updatePatchLogEvents);
    },
    removeValidationResults: (state, { payload: { manualPatchId } }: PayloadAction<{ manualPatchId: string }>) => {
      const patchLogEvents = state[manualPatchId].resourceDictionary;
      const updatePatchLogEvents: Record<string, PatchLogEventPayload> = {};

      for (const id in patchLogEvents) {
        updatePatchLogEvents[id] = {
          ...(patchLogEvents[id] as PatchLogEventPayload),
          validation: undefined,
        };
      }

      Object.assign(state[manualPatchId].resourceDictionary, updatePatchLogEvents);
    },
  },
  idProp: 'id',
});

type ValidationResponse = (PatchLogEventPayload['validation'] & { id: string })[];
type onValidatePayloadAction = PayloadAction<{
  manualPatchId: string;
  data: ValidationResponse;
}>;
type InitialActionsType = typeof slice.actions;
const actions = slice.actions as InitialActionsType &
  CaseReducerActions<
    {
      onValidation: CaseReducer<{}, onValidatePayloadAction>;
      removeValidationResults: CaseReducer<{}, PayloadAction<{ manualPatchId: string }>>;
    },
    'patchLogEvents'
  >;

const revalidate =
  (manualPatchId: string) =>
  async (dispatch: Dispatch<any>, getState: () => ModelRootState, getSocket: () => Socket) => {
    const childState = select(manualPatchId, getState());
    const { subscribed, resourceDictionary: patchLogEvents } = childState || {};
    if (!subscribed || !patchLogEvents) {
      return;
    }

    dispatch(removeValidationResults({ manualPatchId }));

    const socket = getSocket();
    await emitAsyncOrdered(socket, resourceId, `manualPatch/patchLogEvents:validate`, {
      manualPatchId,
    });
  };

const subscribeToValidationResults =
  (manualPatchId: string) =>
  async (dispatch: Dispatch<any>, getState: () => ModelRootState, getSocket: () => Socket) => {
    const childState = select(manualPatchId, getState());
    const { subscribed } = childState || {};
    if (!subscribed) {
      return;
    }

    const socket = getSocket();
    socket.on(`manualPatches/${manualPatchId}/patchLogEvents:validation`, (data: ValidationResponse) => {
      dispatch(onValidation({ manualPatchId, data }));
    });
  };

const unsubscribeFromValidationResults =
  (parentId: string) => async (_: Dispatch<any>, __: () => RootState, getSocket: () => Socket) => {
    const socket = getSocket();
    socket.off(`manualPatches/${parentId}/patchLogEvents:validation`);
  };

const { setLoading, onValidation, removeValidationResults } = actions;

export class SelectPatchLogEventsPayload {
  manualPatchId: string;

  logIds: string[];

  state: boolean;
}

const selectLogEvents =
  ({ manualPatchId, state, logIds }: SelectPatchLogEventsPayload) =>
  async (
    dispatch: Dispatch<any>,
    getState: () => RootState,
    getSocket: () => Socket
  ): Promise<ResponsePayload<null | undefined>> => {
    const socket = getSocket();
    await dispatch(
      setLoadingResources({
        parentId: manualPatchId,
        ids: logIds,
        state: true,
      })
    );

    const response = await emitAsyncOrdered<ResponsePayload<null | undefined>>(
      socket,
      resourceId,
      `manualPatch/patchLogEvents:select`,
      { manualPatchId, state, logIds }
    );

    if (response.status !== 'ok') {
      await dispatch(
        setLoadingResources({
          parentId: manualPatchId,
          ids: logIds,
          state: false,
        })
      );
      dispatch(setError({ status: response.status, msg: response.msg }));
    }

    return response;
  };
export class SelectAllPayload {
  manualPatchId: string;

  state: boolean;
}

const selectAllLogEvents =
  ({ manualPatchId, state }: SelectAllPayload) =>
  async (
    dispatch: Dispatch<any>,
    getState: () => RootState,
    getSocket: () => Socket
  ): Promise<ResponsePayload<null | undefined>> => {
    const account = getState().myAccount.resource;
    const socket = getSocket();
    if (!!account) {
      await dispatch(setLoading({ parentId: manualPatchId, loading: true }));
      const response = await emitAsyncOrdered<ResponsePayload<null | undefined>>(
        socket,
        resourceId,
        `manualPatch/patchLogEvents:selectAll`,
        { manualPatchId, state }
      );
      await dispatch(setLoading({ parentId: manualPatchId, loading: false }));

      if (response.status !== 'ok') {
        dispatch(setError({ status: response.status, msg: response.msg }));
      }

      return response;
    } else {
      dispatch(setError({ status: 'unauthenticated' }));
      return {
        status: 'unauthenticated',
      };
    }
  };

export class ReassignPatchLogEventsPayload {
  manualPatchId: string;
  logIds: string[];
  userId: string;
}

const reassignLogsToUser =
  ({ manualPatchId, logIds, userId }: ReassignPatchLogEventsPayload) =>
  async (
    dispatch: Dispatch<any>,
    getState: () => RootState,
    getSocket: () => Socket
  ): Promise<ResponsePayload<null | undefined>> => {
    const socket = getSocket();
    await dispatch(
      setLoadingResources({
        parentId: manualPatchId,
        ids: logIds,
        state: true,
      })
    );

    const response = await emitAsyncOrdered<ResponsePayload<null | undefined>>(
      socket,
      resourceId,
      `manualPatch/patchLogEvents:reassign`,
      { manualPatchId, logIds, userId },
      false
    );

    await dispatch(
      setLoadingResources({
        parentId: manualPatchId,
        ids: logIds,
        state: false,
      })
    );

    if (response.status !== 'ok') {
      dispatch(setError({ status: response.status, msg: response.msg }));
    }

    return response;
  };

export class UpdateLogEventPayload {
  manualPatchId: string;
  id: string;
  event: LogEvent;
}

const updateLogEvent =
  ({ manualPatchId, id, event }: UpdateLogEventPayload) =>
  async (
    dispatch: Dispatch<any>,
    getState: () => RootState,
    getSocket: () => Socket
  ): Promise<ResponsePayload<null | undefined>> => {
    const socket = getSocket();
    await dispatch(
      setLoadingResources({
        parentId: manualPatchId,
        ids: [id],
        state: true,
      })
    );

    const response = await emitAsyncOrdered<ResponsePayload<null | undefined>>(
      socket,
      resourceId,
      `manualPatch/patchLogEvents:update`,
      { manualPatchId, id, event },
      false
    );

    if (response.status !== 'ok') {
      await dispatch(
        setLoadingResources({
          parentId: manualPatchId,
          ids: [id],
          state: false,
        })
      );
      dispatch(setError({ status: response.status, msg: response.msg }));
    }

    return response;
  };

export class DuplicateLogEventPayload {
  manualPatchId: string;
  logId: string;
}

const duplicateLogEvent =
  ({ manualPatchId, logId }: DuplicateLogEventPayload) =>
  async (
    dispatch: Dispatch<any>,
    getState: () => RootState,
    getSocket: () => Socket
  ): Promise<ResponsePayload<null | undefined>> => {
    const socket = getSocket();
    await dispatch(
      setLoadingResources({
        parentId: manualPatchId,
        ids: [logId],
        state: true,
      })
    );

    const response = await emitAsyncOrdered<ResponsePayload<null | undefined>>(
      socket,
      resourceId,
      `manualPatch/patchLogEvents:duplicate`,
      { manualPatchId, logId },
      false
    );

    if (response.status !== 'ok') {
      await dispatch(
        setLoadingResources({
          parentId: manualPatchId,
          ids: [logId],
          state: false,
        })
      );
      dispatch(setError({ status: response.status, msg: response.msg }));
    }

    return response;
  };

export class AdjustLogEventsOdometerPayload {
  manualPatchId: string;
  offset: number;
}
const adjustLogEventsOdometer =
  ({ manualPatchId, offset }: AdjustLogEventsOdometerPayload) =>
  async (
    dispatch: Dispatch<any>,
    getState: () => RootState,
    getSocket: () => Socket
  ): Promise<ResponsePayload<null | undefined>> => {
    const socket = getSocket();
    dispatch(setLoading({ parentId: manualPatchId, loading: true }));

    const response = await emitAsyncOrdered<ResponsePayload<null | undefined>>(
      socket,
      resourceId,
      `manualPatch/patchLogEvents:adjustOdometer`,
      { manualPatchId, offset },
      false
    );
    dispatch(setLoading({ parentId: manualPatchId, loading: false }));

    if (response.status !== 'ok') {
      dispatch(setError({ status: response.status, msg: response.msg }));
    }

    return response;
  };

export default slice.reducer;

export {
  subscribeToValidationResults,
  unsubscribeFromValidationResults,
  revalidate,
  selectLogEvents,
  selectAllLogEvents,
  reassignLogsToUser,
  updateLogEvent,
  duplicateLogEvent,
  adjustLogEventsOdometer,
  select,
  selectChildResourceList,
  unsubscribe,
  reducer,
  reconnect,
  onPublish,
  onPartial,
  subscribe,
  slice,
};

export const usePatchLogEvents = (manualPatchId: string | null) => {
  const {
    resourceDictionary: patchLogEventsById,
    loading: patchLogEventsLoading,
    subscribed: patchLogEventsSubscribed,
  } = useSelector(R.partial(select, [manualPatchId])) || {};

  const patchLogEvents = useSelector(R.partial(selectChildResourceList, [manualPatchId])) as
    | PatchLogEventPayload[]
    | undefined;

  return {
    patchLogEvents: patchLogEvents as PatchLogEventPayload[] | undefined,
    patchLogEventsById: patchLogEventsById as Record<string, PatchLogEventPayload> | undefined,
    patchLogEventsLoading,
    patchLogEventsSubscribed,
  };
};
